import React from "react";
import { useNavigate } from "react-router-dom";
// import aboutbanner from "../assets/aboutbanner.jpg";
// import PosterOne from "../share/component.jsx/posterone";
// import newimg from '../assets/banner/1.jpeg';





/* ─── Data ──────────────────────────────────────────────────────────── */

const symptomCards = [
  {
    title: 'Chest Pain & Pressure',
    desc: 'Tightness, squeezing, or pressure-like pain in the chest — may extend to the jaw, neck, shoulders, or back.',
    iconBg: '#FADBD8',
    iconColor: '#C0392B',
    iconPath: require('../assets/symptoms/chestPainNPressure.png'),
  },
  {
    title: 'Coronary Artery Disease',
    desc: 'Fatty plaque narrows coronary arteries, restricting oxygen to the heart — especially under physical or emotional stress.',
    iconBg: '#FAEEDA',
    iconColor: '#854F0B',
    iconPath: require('../assets/symptoms/coronary.png'),
  },
  {
    title: 'Angina',
    desc: 'Chest pain caused by reduced blood flow to the heart muscle. May feel like squeezing, heaviness, or pressure.',
    iconBg: '#EAF3DE',
    iconColor: '#3B6D11',
    iconPath: require('../assets/symptoms/angina.png'),
  },
  {
    title: 'Poor Circulation',
    desc: 'Cold hands or feet, leg pain during walking, or numbness may signal peripheral artery disease or wider atherosclerosis.',
    iconBg: '#E6F1FB',
    iconColor: '#185FA5',
    iconPath: require('../assets/symptoms/circulation.png'),
  },
];

const symptomsEvaluated = [
  'Chest tightness, pain, or pressure',
  'Pain when breathing in or out, during activity, or at rest',
  'Symptoms triggered by exercise, stress, or cold weather',
  'Pain that travels to the arm, jaw, neck, or upper back',
  'Pain, numbness, or coldness of the legs indicating poor circulation',
  'Chest symptoms accompanied by weakness, dizziness, sweating, or nausea',
];

const triageReviews = [
  'The sensation of the pain or pressure',
  'Where it is situated and whether it spreads to other areas',
  'Presence or absence of symptoms during activity or rest',
  'Duration and pattern of symptoms over time',
  'Accompanying symptoms: nausea, sweating, dizziness, or breathlessness',
  'Risk factors: age, smoking, diabetes, high blood pressure, and high cholesterol',
];

const qaItems = [
  {
    q: 'Is angina the same as a heart attack?',
    a: 'No — angina is chest pain from reduced blood flow, not a heart attack. However, unstable angina can signal an impending heart attack and requires urgent evaluation.',
  },
  {
    q: 'Can chest pain be non-cardiac?',
    a: 'Yes. Chest discomfort can stem from acid reflux, anxiety, lung conditions, or musculoskeletal issues. A full symptom pattern helps distinguish cardiac from non-cardiac causes.',
  },
  {
    q: 'When is shortness of breath an emergency?',
    a: 'When accompanied by chest pressure, dizziness, sweating, or sudden weakness — seek emergency care immediately.',
  },
];

/* ─── Sub-components ────────────────────────────────────────────────── */

function HeroBanner() {
  return (
    <div className="ihb-hero">
      <img
        src={require('../assets/symptoms/chestPain.png')}
        alt="Chest pain and cardiovascular care"
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
      <div className="ihb-hero-overlay" />
      <div className="ihb-hero-content">
        <span className="ihb-hero-tag">Cardiovascular Health</span>
        <h1>Chest Pain &amp; Circulatory Issues</h1>
        <p>
          From angina to coronary artery disease — understanding what your chest
          pain means and when to act.
        </p>
      </div>
    </div>
  );
}

function Byline() {
  return (
    <div className="ihb-byline">
      <div className="ihb-byline-dot">CV</div>
      <div className="ihb-byline-text">
        <strong>Cardiovascular Physician — Dr. Siam Al Mobarak</strong>
        <span>
          Expert guidance on chest pain, coronary artery disease &amp; circulation
        </span>
      </div>
    </div>
  );
}

function SymptomCard({ title, desc, iconBg, iconColor, iconPath }) {
  return (
    <div className="ihb-rhythm-card">
      <div className="ihb-icon" >
        {typeof iconPath === 'string' && (iconPath.endsWith('.png') || iconPath.endsWith('.jpg') || iconPath.endsWith('.jpeg') || iconPath.endsWith('.svg')) ? (
          <img src={iconPath} alt="" />
        ) : (
          <svg
            width="30"
            height="30"
            viewBox="0 0 24 24"
            fill="none"
            stroke={iconColor}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d={iconPath} />
          </svg>
        )}
      </div>
      <h3>{title}</h3>
      <p>{desc}</p>
    </div>
  );
}

function QAItem({ q, a }) {
  return (
    <div className="ihb-qa-item">
      <div className="ihb-qa-q">{q}</div>
      <div className="ihb-qa-a">{a}</div>
    </div>
  );
}

/* ─── Main Page Component ───────────────────────────────────────────── */

export default function ChestPainPage() {
  const navigate = useNavigate();
  return (
    <div className="ihb-body">

      {/* ── Hero ── */}
      <HeroBanner />

      <div className="ihb-page">

        {/* ── Byline ── */}
        <Byline />

        {/* ── Lead ── */}
        <p className="ihb-lead">
          Chest pain may be frightening. So can tightness, pressure, shortness of
          breath, or cold hands and feet. A lot of individuals are concerned that
          these symptoms are indicative of a heart issue, and in some cases, such
          fears are justified. Circulation issues, angina, and coronary artery
          disease could decrease the flow of blood and oxygen supply, manifesting
          as chest pains, shortness of breath, fatigue, or limb pain. Coronary heart
          disease occurs when plaque narrows the heart's arteries, and angina is the
          chest pain that is brought about by insufficient blood flow to the heart
          muscle. Our online assessment will enable you to move to the next stage
          with confidence.
        </p>

        {/* ── Overview ── */}
        <section className="ihb-section">
          <h2>Understanding Chest Pain &amp; Circulation</h2>
          <p>
            It is created to help those individuals who need quick information about
            whether the symptoms are related to the heart, whether they require
            emergency treatment, and what kind of follow-up might be required. It is
            not used as an emergency treatment; however, it can assist you in
            realizing when your chest pain or other symptoms of circulation are
            serious. Angina and coronary artery disease are often characterized by
            the presence of chest pains and shortness of breath, but some
            individuals may have the disease with no symptoms or atypical symptoms.
          </p>
        </section>

        {/* ── Condition Cards ── */}
        <section className="ihb-section">
          <h2>Conditions We Help Evaluate</h2>
          <div className="ihb-card-grid">
            {symptomCards.map((card) => (
              <SymptomCard key={card.title} {...card} />
            ))}
          </div>
        </section>

        {/* ── Symptoms List ── */}
        <section className="ihb-section">
          <h2>Symptoms We Help Evaluate</h2>
          <p>
            Symptoms of the heart are not necessarily dramatic. Others experience a
            heavy, squeezing, or pressure-like pain in the chest. Others observe
            burning, pain in the jaw, pain in the upper back, sweating, unusual
            fatigue, or breathlessness when active. Angina can be said to feel like a
            squeezing, heavy, tight, or even pressurizing sensation in the chest,
            and the symptoms may extend to the neck, jaw, shoulders, arms, or back.
          </p>
          <p>
            The issues that we usually evaluate include:
          </p>
          <ul className="ihb-treatment-list">
            {symptomsEvaluated.map((item) => (
              <li key={item}>
                <span className="ihb-bullet" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
          <p style={{ marginTop: '1rem' }}>
            Narrowed arteries outside the heart can also be indicative of poor
            circulation. According to Mayo Clinic, constricted blood vessels can
            lead to pain, numbness, weakness, or coldness in limbs or legs.
          </p>
        </section>

        {/* ── CAD ── */}
        <section className="ihb-section">
          <h2>Coronary Artery Disease (CAD)</h2>
          <p>
            One of the most frequent causes of heart-related chest symptoms is
            coronary artery disease. It occurs when fatty deposits, known as plaque,
            accumulate in the inside of the coronary arteries and limit the amount of
            blood that gets to the heart muscle. This can restrict the amount of
            oxygen, particularly when your heart is laboring more intensely, as in
            walking, ascending stairs, or under stress.
          </p>
          <p>
            CAD may develop gradually over a number of years. Chest discomfort or
            shortness of breath is the first red flag in certain instances. A heart
            attack can be the initial hint in other instances. This is why it is
            important to be evaluated early. Our online diagnosis assists with the
            screening of the patterns that could match CAD, and identifies the
            symptoms that require immediate or urgent care.
          </p>
        </section>

        {/* ── Callout ── */}
        <div className="ihb-callout" role="note">
          <p>
            <strong>Note:</strong> Angina cannot be neglected. Although stable
            angina may be managed through lifestyle changes and medication, unstable
            angina is more dangerous — it may be an indication of an impending heart
            attack. Even when pain is absent, a medical examination is still
            necessary.
          </p>
        </div>

        {/* ── Angina ── */}
        <section className="ihb-section">
          <h2>Angina</h2>
          <p>
            Angina is not a disease in itself. It is one of the symptoms that
            normally indicates a lack of oxygen-rich blood to the heart. It can
            present itself in the form of pressure, a squeeze, heaviness, or pain in
            the chest. Angina can be mild to some individuals. To some, it becomes
            uncomfortable and scary. It can occur during workouts as well as
            emotional stress and can be relieved through rest.
          </p>
          <p>
            Angina is something that cannot be neglected. Although stable angina may
            be treated through lifestyle changes, drugs, and in some cases,
            procedures, unstable angina is more dangerous in that it may be an
            indication of a heart attack. The pain may not be present, but it still
            needs a medical examination.
          </p>
        </section>

        {/* ── Shortness of Breath ── */}
        <section className="ihb-section">
          <h2>Shortness of Breath</h2>
          <p>
            One of the most frequent reasons why people want to have their hearts
            evaluated is shortness of breath. It can be accompanied by chest pains,
            or it can be spontaneous. Coronary artery disease, angina, heart attack,
            heart failure, lung disease, anxiety, or other conditions can be
            associated with breathlessness. This is why the complete symptom pattern
            is important.
          </p>
          <p>
            When you are too tired to talk in a complete sentence, are unable to
            comfortably talk, feel breathless accompanied by chest pressure, dizzy,
            or sweaty, the case might be an emergency. These red-flag combinations
            are prioritized in our triage process to make you understand what kind of
            care to get (immediate, same-day medical review, or follow-up).
          </p>
        </section>

        {/* ── Poor Circulation ── */}
        <section className="ihb-section">
          <h2>Poor Circulation</h2>
          <p>
            Another symptom that individuals tend to associate with heart health,
            and with justification, is poor circulation. The shortage of blood supply
            may result in cold hands or feet, pain in the legs during walking,
            numbness, or slow recovery. These problems might be connected with
            peripheral artery disease or more extensive atherosclerosis, which is
            also the process of plaque that may occur in the arteries of the heart.
          </p>
          <p>
            In case of poor circulation along with chest pain, fatigue, diabetes,
            high blood pressure, history of smoking or high cholesterol, a detailed
            cardiovascular care can be required. Online review assists in
            determining at what point the circulation symptoms can lead to a
            heart-centered workup.
          </p>
        </section>

        {/* ── Triage Section ── */}
        <section className="ihb-section">
          <h2>How Our Triage Helps?</h2>
          <p>
            The aim is clarity. We assist you in classifying symptoms into the
            appropriate level of concern through reviewing:
          </p>
          <ul className="ihb-treatment-list">
            {triageReviews.map((item) => (
              <li key={item}>
                <span className="ihb-bullet" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
          <p style={{ marginTop: '1rem' }}>
            This could help determine a pattern that is more consistent with your
            symptoms that makes more sense to angina, which could be coronary artery
            disease, non-cardiac chest discomfort, cardiovascular disease,
            Circulatory System Diseases, or a circulation issue that still needs
            follow-up.
          </p>
        </section>

        {/* ── Q&A ── */}
        <section className="ihb-section">
          <h2>Common Questions</h2>
          <div className="ihb-qa-section">
            {qaItems.map((item) => (
              <QAItem key={item.q} {...item} />
            ))}
          </div>
        </section>

        {/* ── Urgent Box ── */}
        <div className="ihb-urgent-box" role="alert">
          <h2>When to Seek Emergency Care</h2>
          <p>
            Do not use online triage when the symptoms are severe. Chest pain, which
            may be crushing/tight, spreading to the arm, neck, jaw, or back, severe
            shortness of breath, fainting, or cold sweat, nausea, or sudden weakness
            require emergency assistance. These can be symptoms of a heart attack or
            some other emergency.
          </p>
        </div>

        {/* ── Take Next Step ── */}
        <section className="ihb-section">
          <h2>Take the Next Step</h2>
          <p>
            Chest pain and circulation symptoms are perplexing, yet they must not be
            swept away. It can be suspected of coronary artery disease treatment,
            angina, lack of breath, or poor circulation, but the fact is that timely
            examination can save lives. Our service is the first step that is easily
            taken to enable you to know your symptoms and select the appropriate
            level of care through trusted cardiovascular clinics.
          </p>
          <p>
            Get your assessment started today, in case your symptoms are recent,
            increasing, or alarming. And in case they are harsh or acute, go to the
            doctor immediately.
          </p>
        </section>

        {/* ── Footer CTA ── */}
        <div className="ihb-footer-cta">
          <p>
            Concerned about chest pain, shortness of breath, or poor circulation?
            Start your online assessment today and take the first step toward clarity,
            confidence, and the right level of care.
          </p>
          <button className="ihb-cta-btn" onClick={() => navigate('/consultations')}>Start Your Assessment</button>
        </div>


        

      </div>

    </div>
  );
}